import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, ListTodo, Check, AlertTriangle, Trash2, X, ChevronDown } from "lucide-react";

function fmtDue(d) {
  if (!d) return null;
  try {
    const dt = new Date(d);
    const day = dt.getDate();
    const s = day % 10 === 1 && day !== 11 ? "st" : day % 10 === 2 && day !== 12 ? "nd" : day % 10 === 3 && day !== 13 ? "rd" : "th";
    return `${day}${s} ${dt.toLocaleString("en-GB", { month: "short" })}`;
  } catch { return d; }
}

export default function TaskTodos({ legacyId }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ title: "", description: "", due_date: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/tasks/${legacyId}/todos`).then((r) => setItems(r.data.items || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, [legacyId]);

  const add = async () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/tasks/${legacyId}/todos`, f);
      setItems((s) => [...s, r.data.todo]);
      setF({ title: "", description: "", due_date: "" }); setAdding(false);
      toast.success("To-do added");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add to-do"); }
    finally { setBusy(false); }
  };

  const patch = async (t, fields) => {
    try {
      const r = await api.patch(`/tasks/${legacyId}/todos/${t.id}`, fields);
      setItems((s) => s.map((x) => (x.id === t.id ? r.data.todo : x)));
    } catch { toast.error("Could not update to-do"); }
  };

  const remove = async (t) => {
    try { await api.delete(`/tasks/${legacyId}/todos/${t.id}`); setItems((s) => s.filter((x) => x.id !== t.id)); }
    catch { toast.error("Could not remove to-do"); }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="task-todos">
      <div className="flex items-center gap-2 px-5 py-3.5 bg-slate-50/70 border-b border-slate-100">
        <button type="button" onClick={() => setOpen((o) => !o)} className="flex-1 flex items-center gap-2 text-left text-base font-bold text-slate-900" data-testid="task-todos-toggle">
          <ListTodo className="w-4 h-4 text-indigo-500" /> To Dos
          <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-semibold" data-testid="task-todos-count">{items.length}</span>
        </button>
        <button type="button" onClick={() => { setOpen(true); setAdding(true); }} className="text-xs font-semibold text-indigo-600 hover:text-indigo-800" data-testid="task-todos-add">+ Add</button>
        <button type="button" onClick={() => setOpen((o) => !o)} aria-label="Toggle To Dos" data-testid="task-todos-toggle-icon" className="text-slate-400 hover:text-slate-600">
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
      </div>

      {open && (<div className="px-5 py-5">
      {adding && (
        <div className="mb-3 rounded-xl border border-slate-200 p-3 space-y-2" data-testid="task-todo-form">
          <Input value={f.title} onChange={(e) => setF((s) => ({ ...s, title: e.target.value }))} placeholder="Title" data-testid="task-todo-title" />
          <Input value={f.description} onChange={(e) => setF((s) => ({ ...s, description: e.target.value }))} placeholder="Description" data-testid="task-todo-desc" />
          <div className="flex items-center gap-2">
            <Input type="date" value={f.due_date} onChange={(e) => setF((s) => ({ ...s, due_date: e.target.value }))} className="max-w-[180px]" data-testid="task-todo-due" />
            <div className="ml-auto flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={add} data-testid="task-todo-save">
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Save
              </Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-400 py-4 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center text-sm text-slate-400 py-4">No to-dos yet.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((t) => (
            <div key={t.id} className="flex items-center gap-3 py-2.5" data-testid={`task-todo-${t.id}`}>
              <div className="min-w-0 flex-1">
                {t.description ? <div className="text-xs text-slate-400 truncate">{t.title}</div> : null}
                <div className={`text-sm font-semibold truncate ${t.done ? "line-through text-slate-400" : "text-slate-800"}`}>{t.description || t.title}</div>
              </div>
              {t.due_date && <span className="text-xs font-medium text-slate-600 border border-slate-200 rounded-full px-2.5 py-1 shrink-0">{fmtDue(t.due_date)}</span>}
              <button onClick={() => patch(t, { done: !t.done })} title="Complete" data-testid={`task-todo-done-${t.id}`}
                className={`w-7 h-7 rounded-full border flex items-center justify-center shrink-0 ${t.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-slate-300 text-slate-400 hover:border-emerald-400 hover:text-emerald-500"}`}>
                <Check className="w-4 h-4" />
              </button>
              <button onClick={() => patch(t, { urgent: !t.urgent })} title="Urgent" data-testid={`task-todo-urgent-${t.id}`}
                className={`w-7 h-7 rounded-full border flex items-center justify-center shrink-0 ${t.urgent ? "bg-amber-500 border-amber-500 text-white" : "border-slate-300 text-slate-400 hover:border-amber-400 hover:text-amber-500"}`}>
                <AlertTriangle className="w-4 h-4" />
              </button>
              <button onClick={() => remove(t)} title="Delete" data-testid={`task-todo-del-${t.id}`} className="text-slate-300 hover:text-rose-500 shrink-0"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
      </div>)}
    </section>
  );
}
